import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { createHttpParams } from '~/infrastructure/http/params';
import { augmentPaging, DataWithPagingMetadata } from '~/infrastructure/paging';
import {
  MaintenanceBlacklistCheckResultDto,
  MeasureWhenPerformedDto,
  PerformedMaintenanceDto,
  PerformedMaintenanceGridDataModel,
  PerformedMaintenanceHistorySummaryDto,
  PerformMaintenanceRequestModel,
  PerformMaintenanceActionModel
} from '~/models/modules/Asset/Transactions/Maintenance/maintenance.model';
import { ApiServiceBase, API_NAMES } from '~/services/api';

@Injectable({ providedIn: 'root' })
export class PerformMaintenanceService extends ApiServiceBase {
  constructor(injector: Injector, private http: HttpClient) {
    super(injector);
    this.setBase(API_NAMES.assets);
  }

  getMeasuresWhenPerformed(): Observable<MeasureWhenPerformedDto[]> {
    return this.http.get<MeasureWhenPerformedDto[]>(this.url('/PerformMaintenance/measures'));
  }

  performMaintenance(model: PerformMaintenanceRequestModel): Observable<PerformedMaintenanceDto> {
    return this.http.post<PerformedMaintenanceDto>(this.url('/PerformMaintenance'), model);
  }

  performAction(model: PerformMaintenanceActionModel): Observable<PerformedMaintenanceDto> {
    return this.http.put<PerformedMaintenanceDto>(this.url('/PerformMaintenance/perform-action'), model);
  }

  getHistoryGridData(
    paramsObj?: any,
    gridModel?: any
  ): Observable<DataWithPagingMetadata<PerformedMaintenanceHistorySummaryDto>> {
    let params: HttpParams;
    if (typeof paramsObj === 'string') {
      params = new HttpParams().set('search', paramsObj);
    } else {
      paramsObj = paramsObj || {};
      paramsObj.view = 'summary';
      if (gridModel?.performMaintenanceItemTypeValue && !paramsObj.performMaintenanceItemTypeValue) {
        paramsObj.performMaintenanceItemTypeValue = gridModel.performMaintenanceItemTypeValue;
      }
      if (gridModel?.show && !paramsObj.show) {
        paramsObj.show = gridModel.show;
      }
      params = createHttpParams(paramsObj);
    }

    return this.http
      .post<PerformedMaintenanceHistorySummaryDto[]>(
        this.url('/PerformMaintenance/grid-data'),
        gridModel || {},
        {
          params,
          observe: 'response',
        }
      )
      .pipe(map(augmentPaging));
  }

  getPerformedMaintenanceHistoryGrid(
    paramsObj?: any,
    gridModel?: PerformedMaintenanceGridDataModel
  ): Observable<DataWithPagingMetadata<PerformedMaintenanceHistorySummaryDto>> {
    let params: HttpParams;
    if (typeof paramsObj === 'string') {
      params = new HttpParams().set('search', paramsObj);
    } else {
      paramsObj = paramsObj || {};
      paramsObj.view = 'summary';
      params = createHttpParams(paramsObj);
    }

    return this.http
      .post<PerformedMaintenanceHistorySummaryDto[]>(
        this.url('/PerformedMaintenanceHistory/grid-data'),
        gridModel || {},
        {
          params,
          observe: 'response',
        }
      )
      .pipe(map(augmentPaging));
  }

  getGridData(paramsObj?: any, model?: any): Observable<DataWithPagingMetadata<PerformedMaintenanceHistorySummaryDto>> {
    return this.getHistoryGridData(paramsObj, model);
  }

  getHistoryByAssetId(assetId: number): Observable<PerformedMaintenanceDto[]> {
    return this.http.get<PerformedMaintenanceDto[]>(this.url(`/PerformMaintenance/asset/${assetId}`));
  }

  getHistoryByScheduleId(scheduleId: number): Observable<PerformedMaintenanceDto[]> {
    return this.http.get<PerformedMaintenanceDto[]>(this.url(`/PerformMaintenance/schedule/${scheduleId}`));
  }

  checkCheckoutBlacklist(assetId: number): Observable<MaintenanceBlacklistCheckResultDto> {
    return this.http.get<MaintenanceBlacklistCheckResultDto>(
      this.url(`/PerformMaintenance/checkout-check/${assetId}`)
    );
  }
}
